import { formatDurationClockShort } from "@ui/format";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createMemo, Show } from "solid-js";
import type { VideoEvidence } from "../../ChatEventTypes";
import { isFullVideoEvidence } from "../../utils/videoEvidencePlayback";
import { clipWindow } from "./clipSeek";
import { SeekTrack } from "./SeekTrack";

export const ClipTiming = (props: {
	clip: VideoEvidence;
	playbackProgressPercent?: number;
	onSeek: (clipRelativeSeconds: number) => void;
}) => {
	const fullVideo = () => isFullVideoEvidence(props.clip);
	const progressPercent = () =>
		Math.max(0, Math.min(100, props.playbackProgressPercent ?? 0));

	return (
		<HStack spacing="small" class="clip-timing">
			<Show when={!fullVideo()}>
				<MaterialSymbol symbol="cut" style={{ "font-size": "1rem" }} />
			</Show>
			<Text color="white" family="mono" size="small">
				{formatDurationClockShort(
					fullVideo() ? props.clip.video_duration : props.clip.clip_duration,
				)}
			</Text>
			<SeekTrack
				clip={props.clip}
				playbackProgressPercent={progressPercent()}
				onSeek={props.onSeek}
			>
				<Show
					when={!fullVideo()}
					fallback={
						<FullVideoProgress playbackProgressPercent={progressPercent()} />
					}
				>
					<ClipLocation
						clip={props.clip}
						playbackProgressPercent={progressPercent()}
					/>
				</Show>
			</SeekTrack>
			<Show when={!fullVideo()}>
				<Text color="white" family="mono" size="small">
					of {formatDurationClockShort(props.clip.video_duration)}
				</Text>
			</Show>
		</HStack>
	);
};

const FullVideoProgress = (props: { playbackProgressPercent: number }) => (
	<div class="clip-timing__full-video-progress" aria-hidden="true">
		<div
			class="clip-timing__full-video-progress-fill"
			style={{ width: `${props.playbackProgressPercent}%` }}
		/>
	</div>
);

const ClipLocation = (props: {
	clip: VideoEvidence;
	playbackProgressPercent: number;
}) => {
	const clipLocationStyle = createMemo(() => {
		const { videoDuration, clipStart, clipDuration } = clipWindow(props.clip);

		if (videoDuration <= 0) {
			return {
				left: "0%",
				width: "max(0.3125rem, 100%)",
			};
		}

		const leftPercent = (clipStart / videoDuration) * 100;
		const widthPercent = Math.min(
			100 - leftPercent,
			(clipDuration / videoDuration) * 100,
		);

		return {
			left: `${leftPercent}%`,
			width: `max(0.3125rem, ${widthPercent}%)`,
		};
	});

	const playbackIndicatorStyle = createMemo(() => {
		const percent = Math.max(0, Math.min(100, props.playbackProgressPercent));
		return { width: `${percent}%` };
	});

	return (
		<div
			class="clip-location-video-duration"
			aria-hidden="true"
			title={`Clip at ${formatDurationClockShort(props.clip.source_start_time ?? props.clip.start_time)} of ${formatDurationClockShort(props.clip.video_duration)}`}
		>
			<div class="clip-location" style={clipLocationStyle()}>
				<div class="clip-location__playback" style={playbackIndicatorStyle()} />
			</div>
		</div>
	);
};
